import { Button } from '@ordo/ui/button'
import { cn } from '@ordo/ui/cn'
import { Input } from '@ordo/ui/input'
import { Popover, PopoverContent, PopoverTrigger } from '@ordo/ui/popover'
import { ChevronLeft, ChevronRight } from 'lucide-react'
import { forwardRef, useState } from 'react'

import {
  addDays,
  CUSTOM_RANGE_MAX_DAYS,
  customRangeFromKeys,
  type DateGranularity,
  type DateRange,
  formatRangeLabel,
  isoDate,
  isValidDateKey,
  localDateFromKey,
  rangeDayCount,
  rangeForGranularityToggle,
  rangeWindow,
  reanchorRange,
} from '@/lib/chefs/service-days'

export type { DateGranularity }

interface DateRangeStepperProps {
  range: DateRange
  today: Date
  /**
   * Reports the WHOLE next range in one call. Deliberately not a pair of
   * anchor/granularity callbacks: a granularity toggle changes both at once,
   * and consumers that mirror the range into the URL get only one
   * `setSearchParams` per tick before the closure goes stale — two writes and
   * the first one's param is silently dropped. The custom picker obeys the same
   * rule: it emits one range on submit, not a start write then an end write.
   */
  onRangeChange: (range: DateRange) => void
}

export function DateRangeStepper({ range, today, onRangeChange }: DateRangeStepperProps) {
  const { granularity } = range
  const [customOpen, setCustomOpen] = useState(false)

  const step = (delta: -1 | 1) => {
    // A window walks by its own length, so a 3-day custom range steps 3 days.
    // Weekly is the exception: its window is Mon–Fri but the next one starts 7
    // days out, not 5.
    const days = granularity === 'weekly' ? 7 : rangeDayCount(range)
    onRangeChange(reanchorRange(range, addDays(rangeWindow(range).start, delta * days)))
  }

  const switchGranularity = (next: 'daily' | 'weekly') => {
    if (next === granularity) {
      return
    }
    // Toggling re-anchors to today's day/week so the new mode starts on a
    // familiar position rather than wherever the previous anchor happened to land.
    onRangeChange(rangeForGranularityToggle(next, today))
  }

  const label = formatRangeLabel(range, today)

  return (
    <div className="flex flex-1 items-center justify-between gap-3 lg:flex-initial lg:justify-start">
      <div className="flex items-center gap-1">
        <button
          type="button"
          aria-label="Previous"
          onClick={() => step(-1)}
          className="inline-flex h-8 w-8 items-center justify-center rounded-full text-muted-foreground transition-colors hover:bg-muted/50">
          <ChevronLeft className="h-5 w-5" strokeWidth={1.75} />
        </button>
        <span className="min-w-[7rem] px-1 text-center text-base font-semibold text-foreground">{label}</span>
        <button
          type="button"
          aria-label="Next"
          onClick={() => step(1)}
          className="inline-flex h-8 w-8 items-center justify-center rounded-full text-muted-foreground transition-colors hover:bg-muted/50">
          <ChevronRight className="h-5 w-5" strokeWidth={1.75} />
        </button>
      </div>
      <div className="inline-flex h-9 items-center rounded-full bg-muted p-[3px]">
        <GranularityButton active={granularity === 'daily'} onClick={() => switchGranularity('daily')}>
          Daily
        </GranularityButton>
        <GranularityButton active={granularity === 'weekly'} onClick={() => switchGranularity('weekly')}>
          Weekly
        </GranularityButton>
        <Popover open={customOpen} onOpenChange={setCustomOpen}>
          <PopoverTrigger asChild>
            <GranularityButton active={granularity === 'custom'}>Custom</GranularityButton>
          </PopoverTrigger>
          <PopoverContent align="end" className="w-auto p-3">
            <CustomRangeForm
              // Applying is the ONLY write, so a chef who opens the picker and
              // changes their mind leaves the range alone. Deliberately not
              // "seed a default range when the pill opens": `useChefDateRange`
              // resolves the URL ahead of the store, and its `setSearchParams`
              // write commits a render AFTER the popover opens, so the form
              // would read back the PRE-toggle window and Apply would commit a
              // whole week — the exact thing this feature exists to avoid.
              seed={granularity === 'custom' ? range : rangeForGranularityToggle('custom', today)}
              onApply={(next) => {
                onRangeChange(next)
                setCustomOpen(false)
              }}
            />
          </PopoverContent>
        </Popover>
      </div>
    </div>
  )
}

/**
 * From/To over native date inputs — `min`/`max` let the browser enforce the
 * ordering and the span cap before submit, and `customRangeFromKeys` re-checks
 * both, so a URL that skips this form can't produce an impossible window.
 *
 * Seeded once on mount, which is enough because Radix unmounts the popover's
 * content on close: reopening always re-reads `seed`.
 */
function CustomRangeForm({ seed, onApply }: { seed: DateRange; onApply: (next: DateRange) => void }) {
  const current = rangeWindow(seed)
  const [startKey, setStartKey] = useState(() => isoDate(current.start))
  const [endKey, setEndKey] = useState(() => isoDate(current.end))

  const next = customRangeFromKeys(startKey, endKey)
  const maxEndKey = isValidDateKey(startKey)
    ? isoDate(addDays(localDateFromKey(startKey), CUSTOM_RANGE_MAX_DAYS - 1))
    : undefined

  return (
    <form
      className="flex w-60 flex-col gap-2"
      onSubmit={(event) => {
        event.preventDefault()
        if (next) {
          onApply(next)
        }
      }}>
      <label className="flex items-center justify-between gap-3 text-sm">
        <span className="text-muted-foreground">From</span>
        <Input
          type="date"
          required
          value={startKey}
          onChange={(event) => setStartKey(event.target.value)}
          className="h-8 w-36"
        />
      </label>
      <label className="flex items-center justify-between gap-3 text-sm">
        <span className="text-muted-foreground">To</span>
        <Input
          type="date"
          required
          min={startKey}
          max={maxEndKey}
          value={endKey}
          onChange={(event) => setEndKey(event.target.value)}
          className="h-8 w-36"
        />
      </label>
      <p className="text-xs text-muted-foreground">
        {next
          ? `${rangeDayCount(next)} day${rangeDayCount(next) === 1 ? '' : 's'}`
          : `Pick an end on or after the start — up to ${CUSTOM_RANGE_MAX_DAYS} days.`}
      </p>
      <Button type="submit" size="sm" disabled={!next}>
        Apply
      </Button>
    </form>
  )
}

const GranularityButton = forwardRef<
  HTMLButtonElement,
  React.ComponentProps<'button'> & { active: boolean; children: React.ReactNode }
>(function GranularityButton({ active, children, className, ...props }, ref) {
  return (
    <button
      ref={ref}
      type="button"
      aria-pressed={active}
      className={cn(
        'inline-flex h-full items-center justify-center rounded-full px-4 text-sm font-medium text-foreground transition-colors',
        active && 'bg-background shadow-sm',
        className,
      )}
      {...props}>
      {children}
    </button>
  )
})
